/**
 * Where the session lives.
 *
 * Two tokens, not one: while an admin is impersonating a user, the admin's
 * own token has to survive somewhere so `Stop impersonating` can fall back
 * to it if the short-lived impersonation token expires first (it lasts 30
 * minutes and cannot be refreshed).
 *
 * `localStorage` is a deliberate trade-off. It is readable by any script on
 * this origin, so an XSS bug would expose the token — but the alternative
 * the API supports today is nothing at all, and an httpOnly cookie would
 * need a backend change (CSRF protection, SameSite, a refresh endpoint).
 * Recorded here so the choice is visible rather than accidental; see the
 * README's "Security notes".
 */
const ACCESS_TOKEN = 'tp.accessToken';
const ADMIN_TOKEN = 'tp.adminToken';
const IMPERSONATING = 'tp.impersonating';

export interface ImpersonationState {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  /** Epoch ms when the impersonation token stops working. */
  expiresAt: number;
}

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode / blocked storage: fall back to an in-memory session
    // rather than crashing the app on boot.
    return memory[key] ?? null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    if (value === null) delete memory[key];
    else memory[key] = value;
  }
};

const memory: Record<string, string> = {};

export const tokenStore = {
  getAccessToken: () => read(ACCESS_TOKEN),
  setAccessToken: (token: string | null) => write(ACCESS_TOKEN, token),

  getAdminToken: () => read(ADMIN_TOKEN),
  setAdminToken: (token: string | null) => write(ADMIN_TOKEN, token),

  getImpersonation: (): ImpersonationState | null => {
    const raw = read(IMPERSONATING);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ImpersonationState;
    } catch {
      return null;
    }
  },
  setImpersonation: (state: ImpersonationState | null) =>
    write(IMPERSONATING, state ? JSON.stringify(state) : null),

  clear: () => {
    write(ACCESS_TOKEN, null);
    write(ADMIN_TOKEN, null);
    write(IMPERSONATING, null);
  },
};
